<template>
  <transition name="fade">
    <div
      v-if="open"
      class="tel-ui fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40"
      @click.self="close"
    >
      <div class="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden">
        <!-- header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div class="flex items-center gap-2.5">
            <span class="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAE6FF] text-[#5743BE]">
              <Icon icon="mdi:phone-log" class="text-xl" />
            </span>
            <div>
              <div class="text-sm font-semibold text-slate-800">{{ $t('tel_call_history') }}</div>
              <div class="text-xs text-slate-500">{{ label ? ('#' + label) : '' }}</div>
            </div>
          </div>
          <button type="button" class="text-slate-400 hover:text-slate-700 focus:outline-none" @click="close">
            <Icon icon="mdi:close" class="text-xl" />
          </button>
        </div>

        <!-- tabs -->
        <div v-if="!loading && calls.length" class="flex px-5 pt-3 gap-2">
          <button
            type="button"
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition"
            :class="activeTab === 'outbound' ? 'bg-[#EAE6FF] text-[#5743BE]' : 'text-slate-500 hover:bg-slate-50'"
            @click="activeTab = 'outbound'"
          >
            <Icon icon="mdi:phone-outgoing" />
            {{ $t('tel_tab_outbound') }}
            <span class="px-1.5 rounded-full bg-white/70 text-[10px]">{{ outboundCalls.length }}</span>
          </button>
          <button
            type="button"
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition"
            :class="activeTab === 'inbound' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-50'"
            @click="activeTab = 'inbound'"
          >
            <Icon icon="mdi:phone-incoming" />
            {{ $t('tel_tab_inbound') }}
            <span class="px-1.5 rounded-full bg-white/70 text-[10px]">{{ inboundCalls.length }}</span>
          </button>
        </div>

        <!-- body -->
        <div class="flex-1 overflow-y-auto px-5 py-4">
          <div v-if="loading" class="flex items-center justify-center gap-2 py-10 text-slate-500">
            <Icon icon="eos-icons:three-dots-loading" class="text-2xl" />
            <span class="text-sm">{{ $t('loading_history') }}</span>
          </div>

          <div v-else-if="!calls.length" class="flex flex-col items-center justify-center py-10 text-slate-500">
            <Icon icon="mdi:phone-off-outline" class="text-4xl mb-2 text-slate-400" />
            <p class="text-sm">{{ $t('tel_no_call_history') }}</p>
          </div>

          <div v-else-if="!shownCalls.length" class="flex flex-col items-center justify-center py-10 text-slate-400">
            <Icon icon="mdi:phone-off-outline" class="text-3xl mb-2" />
            <p class="text-sm">{{ activeTab === 'inbound' ? $t('tel_tab_no_inbound') : $t('tel_tab_no_outbound') }}</p>
          </div>

          <div v-else class="flex flex-col gap-3">
            <div v-for="c in shownCalls" :key="c._id" class="rounded-xl border border-slate-200 p-3.5">
              <div class="flex items-center justify-between gap-2 flex-wrap">
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-emerald-100 text-emerald-700': c.callStatus === 'answered',
                    'bg-amber-100 text-amber-700': c.callStatus === 'no-answer' || c.callStatus === 'busy',
                    'bg-red-100 text-red-700': c.callStatus === 'failed',
                    'bg-slate-100 text-slate-600': !c.callStatus,
                  }"
                >
                  <Icon :icon="c.callStatus === 'answered' ? 'mdi:phone-check' : 'mdi:phone-missed'" />
                  {{ c.callStatus ? $t('tel_cs_' + c.callStatus) : $t('tel_cs_unknown') }}
                </span>
                <span class="flex items-center gap-2">
                  <span
                    v-if="c.pending"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                  >
                    <Icon icon="mdi:clock-outline" /> {{ $t('tel_call_pending_outcome') }}
                  </span>
                  <span class="text-xs text-slate-400">{{ fmtDate(c.date) }}</span>
                </span>
              </div>

              <div class="mt-2 flex items-center gap-3 text-xs text-slate-500">
                <span v-if="c.statusName" class="inline-flex items-center gap-1">
                  <Icon icon="mdi:tag-outline" /> {{ c.statusName }}
                </span>
                <span v-if="c.agentName" class="inline-flex items-center gap-1">
                  <Icon icon="mdi:account-outline" /> {{ c.agentName }}
                </span>
                <span v-if="c.duration" class="inline-flex items-center gap-1">
                  <Icon icon="mdi:timer-outline" /> {{ fmtDur(c.duration) }}
                </span>
              </div>

              <!-- Diagnostic hint: what a non-answered outcome likely means
                   (points to the line/SIM vs the customer). -->
              <p
                v-if="c.callStatus && c.callStatus !== 'answered'"
                class="mt-1.5 flex items-start gap-1.5 text-[11px]"
                :class="c.callStatus === 'failed' ? 'text-red-600' : 'text-amber-600'"
              >
                <Icon :icon="c.callStatus === 'failed' ? 'mdi:sim-alert' : 'mdi:information-outline'" class="text-sm flex-shrink-0 mt-0.5" />
                <span>{{ $t('tel_cs_hint_' + c.callStatus) }}</span>
              </p>

              <p v-if="c.comment" class="mt-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                {{ c.comment }}
              </p>

              <!-- Show the audio player only when there's real audio to hear: an
                   ANSWERED call, OR a legacy row with no callStatus (older recordings
                   predate the field but have valid audio). A no-answer/voicemail/busy
                   inbound call still carries a recordingUrl (MixMonitor starts on
                   Ringing → a tiny silent WAV is uploaded), so gating on recordingUrl
                   alone rendered an empty player — the bug this fixes. -->
              <AudioPlayer
                v-if="c.recordingUrl && (c.callStatus === 'answered' || !c.callStatus)"
                :src="c.recordingUrl"
                :initial-duration="c.duration"
                class="mt-2.5"
              />
              <!-- No player + no not-answered hint already shown → say why. -->
              <div
                v-else-if="!c.callStatus || c.callStatus === 'answered'"
                class="mt-2 text-xs text-slate-400 italic"
              >
                {{ $t('tel_no_recording') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// App-wide call-history modal. Opened from anywhere via the event bus:
//   bus.$emit('callHistory:open', { orderId, label })
// Fetches telephony/orderCalls (inbound + outbound merged) and shows the list
// with per-call direction/status badges and a recording player. Reused by the
// orders list and any other place that needs to review an order's calls.
import bus from "../../common/eventBus";

export default {
  name: "CallHistoryModal",
  data() {
    return {
      open: false,
      loading: false,
      calls: [],
      orderId: null,
      label: "",
      activeTab: "outbound", // "outbound" (agent→customer) | "inbound" (customer→agent)
    };
  },
  computed: {
    outboundCalls() {
      return this.calls.filter((c) => c.direction !== "inbound");
    },
    inboundCalls() {
      return this.calls.filter((c) => c.direction === "inbound");
    },
    shownCalls() {
      return this.activeTab === "inbound" ? this.inboundCalls : this.outboundCalls;
    },
  },
  mounted() {
    bus.$on("callHistory:open", this.openFor);
  },
  beforeDestroy() {
    bus.$off("callHistory:open", this.openFor);
  },
  methods: {
    async openFor(payload) {
      const orderId = payload && (payload.orderId || payload.id || payload);
      if (!orderId) return;
      this.orderId = orderId;
      this.label = (payload && payload.label) || "";
      this.open = true;
      this.loading = true;
      this.calls = [];
      this.activeTab = "outbound";
      try {
        const res = await this.$server.customPost("telephony/orderCalls", {
          orderId,
          limit: 20,
        });
        const data = res && res.content;
        this.calls = (data && data.calls) || [];
        // Land on the tab that actually has calls (prefer outbound).
        if (!this.outboundCalls.length && this.inboundCalls.length) {
          this.activeTab = "inbound";
        }
      } catch (e) {
        this.calls = [];
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.open = false;
    },
    fmtDate(d) {
      if (!d) return "";
      try {
        return new Date(d).toLocaleString();
      } catch (e) {
        return "";
      }
    },
    fmtDur(seconds) {
      const s = Number(seconds) || 0;
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${m}:${String(r).padStart(2, "0")}`;
    },
  },
};
</script>
